/**
 * Marketing Motion
 *
 * Shared presentation wrappers — blur-fade reveals (Magic UI),
 * soft float, and restrained panel interaction (Hover.dev).
 *
 * Dependencies:
 * - motion/react
 * - lib/utils
 */

'use client'

import { ReactNode, useRef } from 'react'
import {
  motion,
  useInView,
  useMotionTemplate,
  useMotionValue,
  useSpring
} from 'motion/react'

import { cn } from '@/lib/utils'

interface MarketingRevealProps {
  children: ReactNode
  className?: string
  delay?: number
  distance?: number
  blurPx?: number
  viewportMargin?: string
  once?: boolean
}

interface MarketingFloatProps extends MarketingRevealProps {
  amplitude?: number
  duration?: number
  rotate?: number
  scale?: number
}

interface MarketingPanelMotionProps {
  children: ReactNode
  className?: string
  delay?: number
  interactive?: boolean
}

interface MarketingDepthSectionProps {
  children: ReactNode
  className?: string
  intensity?: 'soft' | 'medium'
}

interface MarketingHeroAuraProps {
  className?: string
}

interface MarketingFooterFieldProps {
  className?: string
}

export function MarketingDepthSection({
  children,
  className
}: MarketingDepthSectionProps) {
  return <section className={cn(className)}>{children}</section>
}

/** Blur-fade in view — Magic UI Blur Fade / Motion Primitives text-effect energy. */
export function MarketingReveal({
  children,
  className,
  delay = 0,
  distance = 24,
  blurPx = 8,
  viewportMargin = '-8% 0px -8% 0px',
  once = true
}: MarketingRevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, margin: '-10%' })

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      initial={{ opacity: 0, y: distance, filter: `blur(${blurPx}px)` }}
      animate={
        inView
          ? { opacity: 1, y: 0, filter: 'blur(0px)' }
          : { opacity: 0, y: distance, filter: `blur(${blurPx}px)` }
      }
      transition={{
        duration: 0.55,
        delay,
        ease: [0.22, 1, 0.36, 1]
      }}
      style={{ willChange: 'opacity, transform, filter' }}
    >
      {children}
    </motion.div>
  )
}

export function MarketingHeroAura({ className }: MarketingHeroAuraProps) {
  return (
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      <div className="absolute left-1/2 top-[12%] h-[17rem] w-[54rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(201,213,227,0.48),rgba(255,255,255,0)_68%)] blur-3xl dark:bg-[radial-gradient(circle,rgba(51,78,114,0.14),rgba(10,14,22,0)_72%)]" />
      <div className="absolute left-1/2 top-[32%] h-px w-[58rem] -translate-x-1/2 bg-[linear-gradient(90deg,transparent,rgba(118,140,171,0.42),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(127,160,205,0.16),transparent)]" />
      <div className="absolute left-1/2 top-[8%] size-[28rem] -translate-x-1/2 rounded-full border border-slate-300/35 dark:border-sky-200/5" />
    </div>
  )
}

export function MarketingFloat({
  children,
  className,
  amplitude = 6,
  duration = 5.5
}: MarketingFloatProps) {
  return (
    <motion.div
      className={cn('relative', className)}
      animate={{ y: [0, -amplitude, 0] }}
      transition={{
        duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }}
    >
      {children}
    </motion.div>
  )
}

/** Hover lift + soft spotlight follow — Hover.dev / glare-card energy, restrained. */
export function MarketingPanelMotion({
  children,
  className,
  interactive = false
}: MarketingPanelMotionProps) {
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const spotlightX = useSpring(x, { stiffness: 220, damping: 28 })
  const spotlightY = useSpring(y, { stiffness: 220, damping: 28 })
  const background = useMotionTemplate`radial-gradient(420px circle at ${spotlightX}px ${spotlightY}px, rgba(148,163,184,0.16), transparent 55%)`

  return (
    <motion.div
      className={cn(
        'group relative overflow-hidden transition-transform duration-200 ease-out motion-reduce:transition-none',
        interactive && 'hover:-translate-y-0.5',
        className
      )}
      onMouseMove={
        interactive
          ? event => {
              const rect = event.currentTarget.getBoundingClientRect()
              x.set(event.clientX - rect.left)
              y.set(event.clientY - rect.top)
            }
          : undefined
      }
      onMouseLeave={
        interactive
          ? () => {
              x.set(0)
              y.set(0)
            }
          : undefined
      }
    >
      {interactive ? (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 dark:opacity-0"
          style={{ background }}
        />
      ) : null}
      <div className="relative z-10">{children}</div>
    </motion.div>
  )
}

export function MarketingAmbientField() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] opacity-[0.42] [background-size:180px_180px] [mask-image:radial-gradient(circle_at_top,black,transparent_78%)] dark:opacity-[0.08]" />
      <div className="absolute inset-y-[-8%] left-1/2 w-[34rem] -translate-x-1/2 bg-[linear-gradient(180deg,rgba(255,255,255,0),rgba(200,211,226,0.18)_18%,rgba(200,211,226,0.06)_58%,rgba(255,255,255,0))] blur-3xl dark:bg-[linear-gradient(180deg,rgba(17,24,39,0),rgba(58,89,130,0.08)_20%,rgba(58,89,130,0.03)_56%,rgba(17,24,39,0))]" />
      <div className="absolute left-[-6%] top-36 size-[28rem] rounded-full border border-slate-300/35 blur-[2px] dark:border-sky-200/5" />
      <div className="bg-sky-200/22 absolute left-[4%] top-24 size-56 rounded-full blur-3xl dark:bg-sky-500/5" />
      <div className="bg-stone-200/24 dark:bg-white/3 absolute right-[7%] top-[22rem] size-72 rounded-full blur-3xl" />
      <div className="bg-slate-200/24 absolute bottom-40 left-[18%] h-40 w-[28rem] rounded-full blur-3xl dark:bg-slate-500/5" />
    </div>
  )
}

export function MarketingFooterField({ className }: MarketingFooterFieldProps) {
  return (
    <div className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.18),rgba(255,255,255,0)_24%,rgba(236,240,247,0.32)_100%)] dark:bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(8,14,22,0)_22%,rgba(14,22,34,0.26)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.07)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.07)_1px,transparent_1px)] opacity-[0.34] [background-size:164px_164px] [mask-image:radial-gradient(circle_at_top,black,transparent_78%)] dark:opacity-[0.14]" />
      <div className="absolute left-[4%] top-[-14%] h-[22rem] w-[38rem] rounded-full bg-[radial-gradient(circle,rgba(214,223,235,0.48),rgba(255,255,255,0)_70%)] blur-3xl dark:bg-[radial-gradient(circle,rgba(86,118,162,0.2),rgba(7,12,20,0)_72%)]" />
      <div className="absolute bottom-[-18%] right-[-8%] h-96 w-[34rem] rounded-full bg-[radial-gradient(circle,rgba(207,190,164,0.18),rgba(255,255,255,0)_72%)] blur-3xl dark:bg-[radial-gradient(circle,rgba(117,141,173,0.16),rgba(7,12,20,0)_72%)]" />
      <div className="absolute left-[8%] top-[5.5rem] h-px w-[44%] bg-[linear-gradient(90deg,transparent,rgba(132,151,177,0.52),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(166,195,232,0.26),transparent)]" />
      <div className="absolute right-[8%] top-[15.5rem] h-px w-[34%] bg-[linear-gradient(90deg,transparent,rgba(132,151,177,0.38),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(166,195,232,0.22),transparent)]" />
      <div className="absolute right-[2%] top-[10%] size-[26rem] rounded-full border border-slate-300/30 dark:border-sky-200/10" />
      <div className="bg-slate-200/26 dark:bg-slate-500/8 absolute bottom-[10%] left-[18%] h-40 w-96 rounded-full blur-3xl" />
    </div>
  )
}
